import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/Skeleton";
import type { ReactNode } from "react";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermission?: string;
  requireAdmin?: boolean;
}

export const ProtectedRoute = ({
  children,
  requiredPermission,
  requireAdmin = false,
}: ProtectedRouteProps) => {
  const { user, loading, hasPermission, isAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-primary">
        <Skeleton className="h-4 w-32" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (requireAdmin && !isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-primary px-4">
        <div className="max-w-md rounded-lg border border-accent-red/30 bg-white p-6 text-center shadow-card">
          <h2 className="text-lg font-semibold text-ink-primary">
            Acceso restringido
          </h2>
          <p className="mt-2 text-sm text-ink-secondary">
            Esta sección solo está disponible para administradores.
          </p>
          <a
            href="/"
            className="mt-4 inline-block text-sm font-medium text-accent-blue hover:underline"
          >
            Volver al inicio
          </a>
        </div>
      </div>
    );
  }

  if (requiredPermission && !isAdmin && !hasPermission(requiredPermission)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-primary px-4">
        <div className="max-w-md rounded-lg border border-accent-red/30 bg-white p-6 text-center shadow-card">
          <h2 className="text-lg font-semibold text-ink-primary">
            Sin permisos
          </h2>
          <p className="mt-2 text-sm text-ink-secondary">
            No tienes permisos para acceder a esta sección.
          </p>
          <a
            href="/"
            className="mt-4 inline-block text-sm font-medium text-accent-blue hover:underline"
          >
            Volver al inicio
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export const PublicOnlyRoute = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
};
